import React, { useCallback, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { makeStyles } from '@material-ui/core/styles'

import {
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core'

import {
  Appointment,
  AppointmentStatus,
  defaultHoursOfOperation,
  HoursOfOperation,
  ScheduledHoursOfOperation,
} from 'src/api/models'
import { urlFor } from 'src/routes'

// Layout constants
const HOUR_HEIGHT = 64 // px per hour
const TIME_GUTTER_WIDTH = 64 // px for the time labels column
const MIN_SLOT_MINUTES = 15 // minimum visible appointment height
const MAX_VISIBLE_COLS = 4 // max overlapping columns before collapsing to "+N more"

export type CalendarView = 'day' | 'week' | 'month'

type WeekDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' | 'default'
const daysOfWeek: WeekDay[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const STATUS_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  [AppointmentStatus.scheduled]: { bg: '#E3F2FD', border: '#1565C0', text: '#1565C0' },
  [AppointmentStatus.arrived]: { bg: '#FFF3E0', border: '#E65100', text: '#E65100' },
  [AppointmentStatus.docked]: { bg: '#E8F5E9', border: '#2E7D32', text: '#2E7D32' },
  [AppointmentStatus.completed]: { bg: '#F5F5F5', border: '#757575', text: '#757575' },
}

const DEFAULT_STATUS_COLOR = { bg: '#EEF2FF', border: '#3949AB', text: '#3949AB' }

const useStyles = makeStyles(() => ({
  root: {
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  header: {
    display: 'flex',
    borderBottom: '2px solid #e0e0e0',
    background: '#fafafa',
    flexShrink: 0,
  },
  timeGutterHeader: {
    width: TIME_GUTTER_WIDTH,
    flexShrink: 0,
    borderRight: '1px solid #e0e0e0',
  },
  dayHeader: {
    flex: 1,
    padding: '8px 4px',
    textAlign: 'center',
    borderRight: '1px solid #e0e0e0',
    cursor: 'default',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dayHeaderToday: {
    background: '#E8F0FE',
  },
  dayNumber: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 2px',
    fontWeight: 700,
    fontSize: 16,
  },
  dayNumberToday: {
    background: '#1a73e8',
    color: '#fff',
  },
  capacityBar: {
    height: 4,
    borderRadius: 2,
    background: '#e0e0e0',
    margin: '4px 8px 0',
    overflow: 'hidden',
  },
  capacityFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s ease',
  },
  scrollContainer: {
    overflowX: 'hidden',
  },
  gridBody: {
    display: 'flex',
    position: 'relative',
    minWidth: 0,
  },
  timeGutter: {
    width: TIME_GUTTER_WIDTH,
    flexShrink: 0,
    position: 'relative',
    borderRight: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  timeLabel: {
    position: 'absolute',
    right: 8,
    fontSize: 11,
    color: '#70757a',
    fontWeight: 500,
    userSelect: 'none',
    transform: 'translateY(-50%)',
  },
  daysContainer: {
    flex: 1,
    display: 'flex',
    minWidth: 0,
  },
  dayColumn: {
    flex: 1,
    position: 'relative',
    borderRight: '1px solid #e0e0e0',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  dayColumnToday: {
    background: '#FAFBFF',
  },
  dayColumnClosed: {
    backgroundImage:
      'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.03) 4px, rgba(0,0,0,0.03) 8px)',
    background: '#f9f9f9',
  },
  hourLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTop: '1px solid #e0e0e0',
    pointerEvents: 'none',
  },
  halfHourLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTop: '1px dashed #f0f0f0',
    pointerEvents: 'none',
  },
  nowIndicator: {
    position: 'absolute',
    left: -1,
    right: 0,
    height: 2,
    background: '#EA4335',
    zIndex: 10,
    pointerEvents: 'none',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -4,
      top: -4,
      width: 10,
      height: 10,
      borderRadius: '50%',
      background: '#EA4335',
    },
  },
  appointmentBlock: {
    position: 'absolute',
    borderRadius: 4,
    padding: '2px 4px',
    fontSize: 11,
    lineHeight: 1.3,
    overflow: 'hidden',
    cursor: 'pointer',
    boxSizing: 'border-box',
    borderLeft: '3px solid',
    transition: 'filter 0.15s ease, box-shadow 0.15s ease',
    zIndex: 1,
    '&:hover': {
      filter: 'brightness(0.95)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      zIndex: 2,
    },
  },
  appointmentName: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  appointmentTime: {
    opacity: 0.85,
    whiteSpace: 'nowrap',
  },
  popoverPaper: {
    width: 280,
    padding: '12px 16px',
  },
  popoverHeader: {
    marginBottom: 8,
  },
  statusChip: {
    height: 20,
    fontSize: 11,
  },
  detailRow: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  detailLabel: {
    color: '#70757a',
    fontSize: 12,
    minWidth: 60,
    marginRight: 8,
  },
  detailValue: {
    fontSize: 12,
    fontWeight: 500,
  },
  viewLink: {
    marginTop: 12,
    display: 'block',
    width: '100%',
  },
}))

// ── Helpers ──────────────────────────────────────────────────────────────────

const getApptDisplayName = (appt: Appointment): string =>
  appt.asn?.name || appt.truckload?.name || appt.name

type ApptDirection = 'inbound' | 'outbound' | null
const getApptDirection = (appt: Appointment): ApptDirection => {
  if (appt.asn) return 'inbound'
  if (appt.truckload) return 'outbound'
  return null
}

const DIRECTION_STYLES: Record<
  NonNullable<ApptDirection>,
  { label: string; bg: string; color: string }
> = {
  inbound: { label: 'IN', bg: 'rgba(21,101,192,0.15)', color: '#1565C0' },
  outbound: { label: 'OUT', bg: 'rgba(230,81,0,0.15)', color: '#BF360C' },
}

const DirectionBadge = ({
  direction,
  style,
}: {
  direction: ApptDirection
  style?: React.CSSProperties
}) => {
  if (!direction) return null
  const s = DIRECTION_STYLES[direction]
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: '0.03em',
        background: s.bg,
        color: s.color,
        borderRadius: 2,
        padding: '0 3px',
        lineHeight: 1.5,
        flexShrink: 0,
        ...style,
      }}
    >
      {s.label}
    </span>
  )
}

const formatHour = (hour: number) => {
  if (hour === 0) return '12 AM'
  if (hour === 12) return '12 PM'
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`
}

const minutesToPx = (minutes: number) => (minutes / 60) * HOUR_HEIGHT

const formatMinuteTime = (minute: number) => {
  const h = Math.floor(minute / 60)
  const m = minute % 60
  const period = h < 12 ? 'AM' : 'PM'
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h
  return m === 0 ? `${displayH} ${period}` : `${displayH}:${String(m).padStart(2, '0')} ${period}`
}

interface DragState {
  type: 'move' | 'resize'
  appointment: Appointment
  clickOffsetMinutes: number
  sourceDayIndex: number
  currentDayIndex: number
  currentStartMinute: number
  currentEndMinute: number
  hasMoved: boolean
}

interface PositionedAppointment {
  appointment: Appointment
  col: number
  numCols: number
}

interface OverflowCluster {
  startMinute: number
  endMinute: number
  appointments: Appointment[]
}

// Layout overlapping appointments in a day into columns, capping at maxCols.
// Appointments that would overflow are grouped into time clusters for the "+N more" badge.
// Pass maxCols=Infinity to show all appointments without overflow (used by DayView).
function layoutDayAppointments(
  appointments: Appointment[],
  maxCols: number = MAX_VISIBLE_COLS
): {
  positioned: PositionedAppointment[]
  overflowClusters: OverflowCluster[]
} {
  if (!appointments.length) return { positioned: [], overflowClusters: [] }

  const sorted = [...appointments].sort(
    (a, b) => (a.start?.valueOf() ?? 0) - (b.start?.valueOf() ?? 0) || a.guid.localeCompare(b.guid)
  )

  const assignments: number[] = []
  const columnEnds: moment.Moment[] = []

  for (const appt of sorted) {
    const start = appt.start ?? moment()
    let col = columnEnds.findIndex((end) => start.isSameOrAfter(end))
    if (col === -1) {
      col = columnEnds.length
      columnEnds.push(appt.end ?? moment())
    } else {
      columnEnds[col] = appt.end ?? moment()
    }
    assignments.push(col)
  }

  const totalCols = columnEnds.length
  // When overflow exists, reserve the last visible slot for the overflow badge
  // so it never overlaps a real appointment column.
  const hasOverflow = totalCols > maxCols
  const visibleThreshold = hasOverflow ? maxCols - 1 : maxCols
  const numCols = hasOverflow ? maxCols : totalCols

  const positioned: PositionedAppointment[] = []
  const overflowAppts: Appointment[] = []

  for (let i = 0; i < sorted.length; i++) {
    if (assignments[i] < visibleThreshold) {
      positioned.push({ appointment: sorted[i], col: assignments[i], numCols })
    } else {
      overflowAppts.push(sorted[i])
    }
  }

  // Merge overflow appointments into contiguous time clusters so we show one
  // "+N more" badge per overlapping group rather than one per appointment.
  const overflowClusters: OverflowCluster[] = []
  let clusterEnd: moment.Moment | null = null

  for (const appt of overflowAppts) {
    const start = appt.start ?? moment()
    const end = appt.end ?? moment()
    const startMinute = start.hours() * 60 + start.minutes()
    const endMinute = end.hours() * 60 + end.minutes()

    if (clusterEnd && start.isBefore(clusterEnd)) {
      const last = overflowClusters[overflowClusters.length - 1]
      last.appointments.push(appt)
      if (end.isAfter(clusterEnd)) {
        clusterEnd = end
        last.endMinute = endMinute
      }
    } else {
      overflowClusters.push({ startMinute, endMinute, appointments: [appt] })
      clusterEnd = end
    }
  }

  return { positioned, overflowClusters }
}

// ── Appointment popover ───────────────────────────────────────────────────────

interface AppointmentPopoverProps {
  appointment: Appointment | null
  anchorEl: HTMLElement | null
  onClose: () => void
  onEdit?: () => void
}

const AppointmentPopover = ({
  appointment,
  anchorEl,
  onClose,
  onEdit,
}: AppointmentPopoverProps) => {
  const classes = useStyles()
  if (!appointment) return null

  const statusColor = STATUS_COLORS[appointment.status] ?? DEFAULT_STATUS_COLOR
  const link = urlFor(appointment)

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Paper className={classes.popoverPaper} elevation={0}>
        <Grid container spacing={1} alignItems="center" className={classes.popoverHeader}>
          <Grid item xs>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <DirectionBadge direction={getApptDirection(appointment)} style={{ fontSize: 10 }} />
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                {getApptDisplayName(appointment)}
              </Typography>
            </div>
          </Grid>
          <Grid item>
            <Chip
              label={appointment.status}
              size="small"
              className={classes.statusChip}
              style={{
                background: statusColor.bg,
                color: statusColor.text,
                border: `1px solid ${statusColor.border}`,
              }}
            />
          </Grid>
        </Grid>
        <Divider style={{ marginBottom: 8 }} />
        {appointment.client?.name && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Client</span>
            <span className={classes.detailValue}>{appointment.client.name}</span>
          </div>
        )}
        {appointment.start && appointment.end && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Time</span>
            <span className={classes.detailValue}>
              {appointment.start.format('h:mm A')} – {appointment.end.format('h:mm A')}
            </span>
          </div>
        )}
        {appointment.appointmentType && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Type</span>
            <span className={classes.detailValue}>{appointment.appointmentType}</span>
          </div>
        )}
        {appointment.dock && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Dock</span>
            <span className={classes.detailValue}>{appointment.dock.name}</span>
          </div>
        )}
        {appointment.internalNotes && (
          <div className={classes.detailRow} style={{ alignItems: 'flex-start' }}>
            <span className={classes.detailLabel}>Notes</span>
            <span className={classes.detailValue} style={{ whiteSpace: 'pre-wrap' }}>
              {appointment.internalNotes}
            </span>
          </div>
        )}
        <Grid container spacing={1} style={{ marginTop: 12 }}>
          {onEdit && (
            <Grid item xs={link ? 6 : 12}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                fullWidth
                onClick={() => {
                  onClose()
                  onEdit()
                }}
              >
                Edit
              </Button>
            </Grid>
          )}
          {link && (
            <Grid item xs={onEdit ? 6 : 12}>
              <Button variant="outlined" size="small" color="primary" fullWidth href={link}>
                View
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Popover>
  )
}

// ── Overflow popover ─────────────────────────────────────────────────────────

interface OverflowPopoverProps {
  appointments: Appointment[]
  anchorEl: HTMLElement | null
  onClose: () => void
  onSelect: (appointment: Appointment) => void
}

const OverflowPopover = ({ appointments, anchorEl, onClose, onSelect }: OverflowPopoverProps) => {
  const classes = useStyles()
  if (!appointments.length) return null

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <Paper className={classes.popoverPaper} elevation={0}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          {appointments.length} more appointment{appointments.length !== 1 ? 's' : ''}
        </Typography>
        <Divider style={{ marginBottom: 8 }} />
        {appointments.map((appt) => {
          const colors = STATUS_COLORS[appt.status] ?? DEFAULT_STATUS_COLOR
          return (
            <div
              key={appt.guid}
              className={classes.detailRow}
              style={{
                cursor: 'pointer',
                padding: '4px 6px',
                borderRadius: 4,
                marginBottom: 2,
                borderLeft: `3px solid ${colors.border}`,
                background: colors.bg,
              }}
              onClick={() => {
                onSelect(appt)
              }}
            >
              <div style={{ flex: 1 }}>
                <div className={classes.detailValue}>{appt.name}</div>
                {appt.start && appt.end && (
                  <div className={classes.detailLabel}>
                    {appt.start.format('h:mm A')} – {appt.end.format('h:mm A')}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </Paper>
    </Popover>
  )
}

// ── WeekView ─────────────────────────────────────────────────────────────────

interface ViewProps {
  date: moment.Moment
  defaultHours: HoursOfOperation
  overrideHours?: ScheduledHoursOfOperation
  appointments: Appointment[]
  maxInbound: number
  maxOutbound: number
  onSlotClick?: (start: moment.Moment) => void
  onEdit?: (appointment: Appointment) => void
  onReschedule?: (appointment: Appointment, newStart: moment.Moment, newEnd: moment.Moment) => void
  onDayClick?: (day: moment.Moment) => void
  loading?: boolean
}

const WeekView = (props: ViewProps) => {
  const {
    date,
    defaultHours,
    appointments,
    maxInbound,
    maxOutbound,
    onSlotClick,
    onEdit,
    onReschedule,
    onDayClick,
    loading,
  } = props
  const classes = useStyles()
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null)
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null)
  const [overflowAppts, setOverflowAppts] = useState<Appointment[]>([])
  const [nowMinute, setNowMinute] = useState(() => moment().hours() * 60 + moment().minutes())
  const [hoverSlot, setHoverSlot] = useState<{ dayIndex: number; minute: number } | null>(null)
  const [dragState, setDragState] = useState<DragState | null>(null)

  // Refs for stable access inside document-level event handlers
  const daysContainerRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const onRescheduleRef = useRef(onReschedule)
  onRescheduleRef.current = onReschedule

  // Build the 7 days of the week
  const days: moment.Moment[] = []
  {
    const first = date.clone().weekday(0)
    for (let i = 0; i < 7; i++) {
      days.push(first.clone().add(i, 'days'))
    }
  }

  // Compute visible hour range from facility hours
  const startMinutes: number[] = []
  const endMinutes: number[] = []
  days.forEach((d) => {
    const key = daysOfWeek[d.weekday()]
    const h = defaultHours[key] ?? defaultHours.default
    if (h.open !== h.close) {
      startMinutes.push(h.open)
      endMinutes.push(h.close)
    }
  })
  let rangeStart =
    startMinutes.length > 0 ? Math.floor(Math.min(...startMinutes) / 60) * 60 : 6 * 60
  let rangeEnd = endMinutes.length > 0 ? Math.ceil(Math.max(...endMinutes) / 60) * 60 : 20 * 60

  // Expand range to show any appointments that fall outside the facility hours window
  appointments.forEach((a) => {
    if (a.start) {
      const m = a.start.hours() * 60 + a.start.minutes()
      rangeStart = Math.min(rangeStart, Math.floor(m / 60) * 60)
    }
    if (a.end) {
      const m = a.end.hours() * 60 + a.end.minutes()
      rangeEnd = Math.max(rangeEnd, Math.ceil(m / 60) * 60)
    }
  })

  const totalMinutes = rangeEnd - rangeStart
  const totalHeight = minutesToPx(totalMinutes)

  // Hours to render in the time gutter
  const hours: number[] = []
  for (let m = rangeStart; m <= rangeEnd; m += 60) {
    hours.push(m / 60)
  }

  // Group appointments by day
  const appointmentsByDay = days.map((d) =>
    appointments.filter(
      (a) =>
        a.start &&
        a.end &&
        a.start.isSameOrBefore(d.clone().endOf('day')) &&
        a.end.isSameOrAfter(d.clone().startOf('day'))
    )
  )

  // Keep refs current on every render so document-level handlers always read fresh values
  const rangeStartRef = useRef(rangeStart)
  const rangeEndRef = useRef(rangeEnd)
  const daysRef = useRef(days)
  rangeStartRef.current = rangeStart
  rangeEndRef.current = rangeEnd
  daysRef.current = days

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMinute(moment().hours() * 60 + moment().minutes())
    }, 60000)
    return () => clearInterval(timer)
  }, [])

  // Compute (dayIndex, snapped minute) from any event with clientX/clientY
  const getEventCoords = useCallback(
    (e: { clientX: number; clientY: number }): { dayIndex: number; minute: number } | null => {
      const container = daysContainerRef.current
      if (!container) return null
      const rect = container.getBoundingClientRect()
      const colWidth = rect.width / 7
      const dayIndex = Math.max(0, Math.min(6, Math.floor((e.clientX - rect.left) / colWidth)))
      const rawMinute = rangeStartRef.current + ((e.clientY - rect.top) / HOUR_HEIGHT) * 60
      return { dayIndex, minute: Math.round(rawMinute / 15) * 15 }
    },
    []
  )

  const capacity = maxInbound + maxOutbound

  // Native document-level drag — listeners are attached synchronously in onPointerDown,
  // bypassing React 16's synthetic event delegation which doesn't reliably handle
  // pointer capture scenarios.

  const handleAppointmentPointerDown =
    (appt: Appointment, dayIndex: number) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (!appt.start || !appt.end) return
      e.stopPropagation()
      e.preventDefault()

      const element = e.currentTarget
      const pointerId = e.pointerId
      const blockRect = element.getBoundingClientRect()

      // Determine move vs resize by Y position — bottom 8px triggers resize
      const isResize = blockRect.bottom - e.clientY <= 8
      const clickOffsetMinutes = isResize ? 0 : ((e.clientY - blockRect.top) / HOUR_HEIGHT) * 60

      const state: DragState = {
        type: isResize ? 'resize' : 'move',
        appointment: appt,
        clickOffsetMinutes,
        sourceDayIndex: dayIndex,
        currentDayIndex: dayIndex,
        currentStartMinute: appt.start.hours() * 60 + appt.start.minutes(),
        currentEndMinute: appt.end.hours() * 60 + appt.end.minutes(),
        hasMoved: false,
      }
      dragStateRef.current = state
      setDragState(state)
      document.body.style.cursor = isResize ? 'ns-resize' : 'grabbing'
      document.body.style.userSelect = 'none'

      function onMove(ev: PointerEvent) {
        if (ev.pointerId !== pointerId) return
        ev.preventDefault()
        const current = dragStateRef.current
        if (!current) return
        const coords = getEventCoords(ev)
        if (!coords) return
        let next: DragState
        if (current.type === 'move') {
          const dur = current.currentEndMinute - current.currentStartMinute
          const snapped = Math.round((coords.minute - current.clickOffsetMinutes) / 15) * 15
          const clamped = Math.max(
            rangeStartRef.current,
            Math.min(rangeEndRef.current - dur, snapped)
          )
          next = {
            ...current,
            currentDayIndex: coords.dayIndex,
            currentStartMinute: clamped,
            currentEndMinute: clamped + dur,
            hasMoved: true,
          }
        } else {
          const newEnd = Math.max(
            current.currentStartMinute + 15,
            Math.round(coords.minute / 15) * 15
          )
          next = { ...current, currentEndMinute: newEnd, hasMoved: true }
        }
        dragStateRef.current = next
        setDragState({ ...next })
      }

      function onUp(ev: PointerEvent) {
        if (ev.pointerId !== pointerId) return
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        document.removeEventListener('pointercancel', onUp)
        const current = dragStateRef.current
        if (current?.hasMoved) {
          const suppressClick = (e: MouseEvent) => {
            e.stopPropagation()
            document.removeEventListener('click', suppressClick, true)
          }
          document.addEventListener('click', suppressClick, true)
          if (onRescheduleRef.current) {
            const day = daysRef.current[current.currentDayIndex]
            const newStart = day.clone().startOf('day').add(current.currentStartMinute, 'minutes')
            const newEnd = day.clone().startOf('day').add(current.currentEndMinute, 'minutes')
            onRescheduleRef.current(current.appointment, newStart, newEnd)
          }
        } else if (current) {
          setSelectedAppointment(current.appointment)
          setPopoverAnchor(element)
        }
        dragStateRef.current = null
        setDragState(null)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.addEventListener('pointermove', onMove, { passive: false })
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onUp)
    }

  const snapMinute = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const raw = rangeStart + (y / HOUR_HEIGHT) * 60
    return Math.max(rangeStart, Math.min(rangeEnd - 60, Math.round(raw / 15) * 15))
  }

  const handleColumnClick = (day: moment.Moment) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSlotClick) return
    onSlotClick(day.clone().startOf('day').add(snapMinute(e), 'minutes'))
  }

  const handleColumnMouseMove = (dayIndex: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragStateRef.current) {
      setHoverSlot(null)
      return
    }
    if (!onSlotClick) return
    if ((e.target as HTMLElement).closest('[data-appointment]')) {
      setHoverSlot(null)
      return
    }
    const minute = snapMinute(e)
    setHoverSlot((prev) =>
      prev?.dayIndex === dayIndex && prev?.minute === minute ? prev : { dayIndex, minute }
    )
  }

  const handleColumnMouseLeave = () => setHoverSlot(null)

  const isToday = (d: moment.Moment) => d.isSame(moment(), 'day')

  return (
    <div className={classes.root}>
      {loading && <LinearProgress style={{ flexShrink: 0 }} />}
      {/* Sticky header */}
      <div className={classes.header}>
        <div className={classes.timeGutterHeader} />
        {days.map((d, i) => {
          const today = isToday(d)
          const dayAppts = appointmentsByDay[i]
          const occupancy = capacity > 0 ? Math.min((dayAppts.length / capacity) * 100, 100) : 0
          const occupancyColor =
            occupancy >= 90 ? '#EA4335' : occupancy >= 70 ? '#FBBC04' : '#34A853'
          return (
            <div
              key={i}
              className={`${classes.dayHeader} ${today ? classes.dayHeaderToday : ''}`}
              style={onDayClick ? { cursor: 'pointer' } : undefined}
              onClick={onDayClick ? () => onDayClick(d) : undefined}
              title={onDayClick ? `View ${d.format('dddd, MMMM D')}` : undefined}
            >
              <Typography
                variant="caption"
                style={{ color: today ? '#1a73e8' : '#70757a', fontWeight: 600 }}
              >
                {dayNames[i]}
              </Typography>
              <div
                className={`${classes.dayNumber} ${today ? classes.dayNumberToday : ''}`}
                style={!today ? { color: '#3c4043' } : undefined}
              >
                {d.date()}
              </div>
              {capacity > 0 && (
                <Tooltip
                  title={`${dayAppts.length} / ${capacity} appointments (${Math.round(
                    occupancy
                  )}%)`}
                >
                  <div className={classes.capacityBar}>
                    <div
                      className={classes.capacityFill}
                      style={{ width: `${occupancy}%`, background: occupancyColor }}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
          )
        })}
      </div>

      <div className={classes.scrollContainer}>
        <div className={classes.gridBody} style={{ height: totalHeight }}>
          {/* Time gutter */}
          <div className={classes.timeGutter} style={{ height: totalHeight }}>
            {hours.map((hour) => (
              <div
                key={hour}
                className={classes.timeLabel}
                style={{ top: minutesToPx(hour * 60 - rangeStart) }}
              >
                {formatHour(hour)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          <div className={classes.daysContainer} ref={daysContainerRef}>
            {days.map((d, i) => {
              const key = daysOfWeek[d.weekday()]
              const h = defaultHours[key] ?? defaultHours.default
              const isClosed = h.open === h.close
              const today = isToday(d)
              const { positioned, overflowClusters } = layoutDayAppointments(appointmentsByDay[i])

              return (
                <div
                  key={i}
                  className={`${classes.dayColumn} ${today ? classes.dayColumnToday : ''}`}
                  style={{ height: totalHeight, cursor: onSlotClick ? 'cell' : undefined }}
                  onClick={handleColumnClick(d)}
                  onMouseMove={handleColumnMouseMove(i)}
                  onMouseLeave={handleColumnMouseLeave}
                >
                  {/* Hour & half-hour grid lines */}
                  {hours.map((hour) => (
                    <React.Fragment key={hour}>
                      <div
                        className={classes.hourLine}
                        style={{ top: minutesToPx(hour * 60 - rangeStart) }}
                      />
                      {hour * 60 + 30 < rangeEnd && (
                        <div
                          className={classes.halfHourLine}
                          style={{ top: minutesToPx(hour * 60 + 30 - rangeStart) }}
                        />
                      )}
                    </React.Fragment>
                  ))}

                  {/* Closed / unavailable overlay */}
                  {isClosed && (
                    <div
                      className={classes.dayColumnClosed}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {/* Outside-hours overlays */}
                  {!isClosed && h.open > rangeStart && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: minutesToPx(h.open - rangeStart),
                        background: 'rgba(0,0,0,0.04)',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />
                  )}
                  {!isClosed && h.close < rangeEnd && (
                    <div
                      style={{
                        position: 'absolute',
                        top: minutesToPx(h.close - rangeStart),
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.04)',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />
                  )}

                  {/* Current time indicator */}
                  {today && nowMinute >= rangeStart && nowMinute <= rangeEnd && (
                    <div
                      className={classes.nowIndicator}
                      style={{ top: minutesToPx(nowMinute - rangeStart) }}
                    />
                  )}

                  {/* Hover preview block */}
                  {onSlotClick && hoverSlot?.dayIndex === i && (
                    <div
                      style={{
                        position: 'absolute',
                        top: minutesToPx(hoverSlot.minute - rangeStart),
                        height: minutesToPx(60),
                        left: 2,
                        right: 2,
                        borderRadius: 4,
                        background: 'rgba(26, 115, 232, 0.12)',
                        border: '1.5px dashed rgba(26, 115, 232, 0.45)',
                        pointerEvents: 'none',
                        zIndex: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#1a73e8',
                      }}
                    >
                      {formatMinuteTime(hoverSlot.minute)} –{' '}
                      {formatMinuteTime(hoverSlot.minute + 60)}
                    </div>
                  )}

                  {/* Appointment blocks */}
                  {positioned.map(({ appointment, col, numCols }) => {
                    if (!appointment.start || !appointment.end) return null

                    const startMin = Math.max(
                      appointment.start.hours() * 60 + appointment.start.minutes(),
                      rangeStart
                    )
                    const endMin = Math.min(
                      appointment.end.hours() * 60 + appointment.end.minutes(),
                      rangeEnd
                    )
                    const durationMin = Math.max(endMin - startMin, MIN_SLOT_MINUTES)

                    const top = minutesToPx(startMin - rangeStart)
                    const height = Math.max(minutesToPx(durationMin), 20)
                    const colWidth = 100 / numCols
                    const left = `calc(${col * colWidth}% + 2px)`
                    const width = `calc(${colWidth}% - 4px)`

                    const colors = STATUS_COLORS[appointment.status] ?? DEFAULT_STATUS_COLOR
                    const isDragging = dragState?.appointment.guid === appointment.guid

                    const direction = getApptDirection(appointment)
                    const displayName = getApptDisplayName(appointment)
                    return (
                      <Tooltip
                        key={appointment.guid}
                        title={`${
                          direction ? (direction === 'inbound' ? 'IN' : 'OUT') + ' · ' : ''
                        }${displayName} · ${appointment.start.format(
                          'h:mm A'
                        )}–${appointment.end.format('h:mm A')}`}
                        placement="right"
                      >
                        <div
                          data-appointment
                          className={classes.appointmentBlock}
                          style={{
                            top,
                            height,
                            left,
                            width,
                            background: colors.bg,
                            borderLeftColor: colors.border,
                            color: colors.text,
                            opacity: isDragging ? 0.3 : 1,
                            cursor: isDragging ? 'grabbing' : 'grab',
                            touchAction: 'none',
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onPointerDown={handleAppointmentPointerDown(appointment, i)}
                        >
                          <div className={classes.appointmentName}>{displayName}</div>
                          {height >= 28 && (
                            <div
                              className={classes.appointmentTime}
                              style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                            >
                              <DirectionBadge direction={direction} />
                              {appointment.start.format('h:mm')}–{appointment.end.format('h:mm A')}
                            </div>
                          )}
                          {height >= 44 && appointment.client?.name && (
                            <div className={classes.appointmentTime}>{appointment.client.name}</div>
                          )}
                          {/* Resize handle */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 8,
                              cursor: 'ns-resize',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              touchAction: 'none',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                width: 20,
                                height: 2,
                                borderRadius: 1,
                                background: colors.border,
                                opacity: 0.5,
                              }}
                            />
                          </div>
                        </div>
                      </Tooltip>
                    )
                  })}

                  {/* Drag ghost block */}
                  {dragState?.hasMoved && dragState.currentDayIndex === i && (
                    <div
                      style={{
                        position: 'absolute',
                        top: minutesToPx(dragState.currentStartMinute - rangeStart),
                        height: minutesToPx(
                          dragState.currentEndMinute - dragState.currentStartMinute
                        ),
                        left: 2,
                        right: 2,
                        borderRadius: 4,
                        background: 'rgba(26, 115, 232, 0.18)',
                        border: '2px solid rgba(26, 115, 232, 0.6)',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 4,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 4px',
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#1a73e8',
                      }}
                    >
                      {formatMinuteTime(dragState.currentStartMinute)} –{' '}
                      {formatMinuteTime(dragState.currentEndMinute)}
                    </div>
                  )}

                  {/* Overflow "+N more" badges — pinned to the reserved last column slot */}
                  {overflowClusters.map((cluster, idx) => {
                    const badgeTop = minutesToPx(cluster.startMinute - rangeStart)
                    const badgeHeight = Math.max(
                      minutesToPx(cluster.endMinute - cluster.startMinute),
                      20
                    )
                    const slotFraction = (MAX_VISIBLE_COLS - 1) / MAX_VISIBLE_COLS
                    return (
                      <div
                        key={idx}
                        data-appointment
                        style={{
                          position: 'absolute',
                          top: badgeTop,
                          height: badgeHeight,
                          left: `calc(${slotFraction * 100}% + 2px)`,
                          width: `calc(${100 / MAX_VISIBLE_COLS}% - 4px)`,
                          borderRadius: 4,
                          background: '#E8EAF6',
                          borderLeft: '3px solid #3949AB',
                          color: '#3949AB',
                          fontSize: 10,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          zIndex: 2,
                          boxSizing: 'border-box',
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          setOverflowAppts(cluster.appointments)
                          setOverflowAnchor(e.currentTarget)
                        }}
                      >
                        +{cluster.appointments.length} more
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <AppointmentPopover
        appointment={selectedAppointment}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null)
          setSelectedAppointment(null)
        }}
        onEdit={onEdit && selectedAppointment ? () => onEdit(selectedAppointment) : undefined}
      />
      <OverflowPopover
        appointments={overflowAppts}
        anchorEl={overflowAnchor}
        onClose={() => {
          setOverflowAnchor(null)
          setOverflowAppts([])
        }}
        onSelect={(appt) => {
          const anchor = overflowAnchor
          setOverflowAnchor(null)
          setOverflowAppts([])
          setSelectedAppointment(appt)
          setPopoverAnchor(anchor)
        }}
      />
    </div>
  )
}

// ── DayView ───────────────────────────────────────────────────────────────────

const DayView = (props: ViewProps) => {
  const { date, defaultHours, appointments, onSlotClick, onEdit, onReschedule, loading } = props
  const classes = useStyles()
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null)
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null)
  const [nowMinute, setNowMinute] = useState(() => moment().hours() * 60 + moment().minutes())
  const [hoverSlot, setHoverSlot] = useState<number | null>(null)
  const [dragState, setDragState] = useState<DragState | null>(null)
  // Tick every second so "recently updated" badges stay accurate without any
  // state coordination between the drag/edit action and the data refetch.
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(timer)
  }, [])

  const columnRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState | null>(null)
  const onRescheduleRef = useRef(onReschedule)
  onRescheduleRef.current = onReschedule

  const dayKey = daysOfWeek[date.weekday()] as WeekDay
  const h = defaultHours[dayKey] ?? defaultHours.default

  let rangeStart = h.open !== h.close ? Math.floor(h.open / 60) * 60 : 6 * 60
  let rangeEnd = h.open !== h.close ? Math.ceil(h.close / 60) * 60 : 20 * 60

  appointments.forEach((a) => {
    if (a.start)
      rangeStart = Math.min(
        rangeStart,
        Math.floor((a.start.hours() * 60 + a.start.minutes()) / 60) * 60
      )
    if (a.end)
      rangeEnd = Math.max(rangeEnd, Math.ceil((a.end.hours() * 60 + a.end.minutes()) / 60) * 60)
  })

  const totalMinutes = rangeEnd - rangeStart
  const totalHeight = minutesToPx(totalMinutes)
  const hours: number[] = []
  for (let m = rangeStart; m <= rangeEnd; m += 60) hours.push(m / 60)

  const rangeStartRef = useRef(rangeStart)
  const rangeEndRef = useRef(rangeEnd)
  rangeStartRef.current = rangeStart
  rangeEndRef.current = rangeEnd

  useEffect(() => {
    const timer = setInterval(() => setNowMinute(moment().hours() * 60 + moment().minutes()), 60000)
    return () => clearInterval(timer)
  }, [])

  const isToday = date.isSame(moment(), 'day')
  const { positioned } = layoutDayAppointments(appointments, Infinity)

  const getMinuteFromEvent = useCallback((e: { clientY: number }): number => {
    const col = columnRef.current
    if (!col) return rangeStartRef.current
    const rect = col.getBoundingClientRect()
    const rawMinute = rangeStartRef.current + ((e.clientY - rect.top) / HOUR_HEIGHT) * 60
    return Math.round(rawMinute / 15) * 15
  }, [])

  const handleAppointmentPointerDown =
    (appt: Appointment) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      if (!appt.start || !appt.end) return
      e.stopPropagation()
      e.preventDefault()

      const element = e.currentTarget
      const pointerId = e.pointerId
      const blockRect = element.getBoundingClientRect()
      const isResize = blockRect.bottom - e.clientY <= 8
      const clickOffsetMinutes = isResize ? 0 : ((e.clientY - blockRect.top) / HOUR_HEIGHT) * 60

      const state: DragState = {
        type: isResize ? 'resize' : 'move',
        appointment: appt,
        clickOffsetMinutes,
        sourceDayIndex: 0,
        currentDayIndex: 0,
        currentStartMinute: appt.start.hours() * 60 + appt.start.minutes(),
        currentEndMinute: appt.end.hours() * 60 + appt.end.minutes(),
        hasMoved: false,
      }
      dragStateRef.current = state
      setDragState(state)
      document.body.style.cursor = isResize ? 'ns-resize' : 'grabbing'
      document.body.style.userSelect = 'none'

      function onMove(ev: PointerEvent) {
        if (ev.pointerId !== pointerId) return
        ev.preventDefault()
        const current = dragStateRef.current
        if (!current) return
        const minute = getMinuteFromEvent(ev)
        let next: DragState
        if (current.type === 'move') {
          const dur = current.currentEndMinute - current.currentStartMinute
          const snapped = Math.round((minute - current.clickOffsetMinutes) / 15) * 15
          const clamped = Math.max(
            rangeStartRef.current,
            Math.min(rangeEndRef.current - dur, snapped)
          )
          next = {
            ...current,
            currentStartMinute: clamped,
            currentEndMinute: clamped + dur,
            hasMoved: true,
          }
        } else {
          const newEnd = Math.max(current.currentStartMinute + 15, Math.round(minute / 15) * 15)
          next = { ...current, currentEndMinute: newEnd, hasMoved: true }
        }
        dragStateRef.current = next
        setDragState({ ...next })
      }

      function onUp(ev: PointerEvent) {
        if (ev.pointerId !== pointerId) return
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        document.removeEventListener('pointercancel', onUp)
        const current = dragStateRef.current
        if (current?.hasMoved) {
          const suppressClick = (e: MouseEvent) => {
            e.stopPropagation()
            document.removeEventListener('click', suppressClick, true)
          }
          document.addEventListener('click', suppressClick, true)
          if (onRescheduleRef.current) {
            const newStart = date.clone().startOf('day').add(current.currentStartMinute, 'minutes')
            const newEnd = date.clone().startOf('day').add(current.currentEndMinute, 'minutes')
            onRescheduleRef.current(current.appointment, newStart, newEnd)
          }
        } else if (current) {
          setSelectedAppointment(current.appointment)
          setPopoverAnchor(element)
        }
        dragStateRef.current = null
        setDragState(null)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      document.addEventListener('pointermove', onMove, { passive: false })
      document.addEventListener('pointerup', onUp)
      document.addEventListener('pointercancel', onUp)
    }

  const snapMinute = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const y = e.clientY - rect.top
    const raw = rangeStart + (y / HOUR_HEIGHT) * 60
    return Math.max(rangeStart, Math.min(rangeEnd - 60, Math.round(raw / 15) * 15))
  }

  const sortedAppointments = [...appointments].sort(
    (a, b) => (a.start?.valueOf() ?? 0) - (b.start?.valueOf() ?? 0)
  )

  return (
    <div className={classes.root}>
      {loading && <LinearProgress style={{ flexShrink: 0 }} />}
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.timeGutterHeader} />
        <div
          className={`${classes.dayHeader} ${isToday ? classes.dayHeaderToday : ''}`}
          style={{ flex: 1 }}
        >
          <Typography
            variant="caption"
            style={{ color: isToday ? '#1a73e8' : '#70757a', fontWeight: 600 }}
          >
            {dayNames[date.weekday()]}
          </Typography>
          <div
            className={`${classes.dayNumber} ${isToday ? classes.dayNumberToday : ''}`}
            style={!isToday ? { color: '#3c4043' } : undefined}
          >
            {date.date()}
          </div>
          <Typography variant="caption" style={{ color: '#70757a', fontSize: 11 }}>
            {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
          </Typography>
        </div>
      </div>

      {/* Time grid */}
      <div className={classes.scrollContainer}>
        <div className={classes.gridBody} style={{ height: totalHeight }}>
          <div className={classes.timeGutter} style={{ height: totalHeight }}>
            {hours.map((hour) => (
              <div
                key={hour}
                className={classes.timeLabel}
                style={{ top: minutesToPx(hour * 60 - rangeStart) }}
              >
                {formatHour(hour)}
              </div>
            ))}
          </div>
          <div className={classes.daysContainer}>
            <div
              ref={columnRef}
              className={`${classes.dayColumn} ${isToday ? classes.dayColumnToday : ''}`}
              style={{ height: totalHeight, cursor: onSlotClick ? 'cell' : undefined }}
              onClick={(e) => {
                if (!onSlotClick) return
                onSlotClick(date.clone().startOf('day').add(snapMinute(e), 'minutes'))
              }}
              onMouseMove={(e) => {
                if (dragStateRef.current) {
                  setHoverSlot(null)
                  return
                }
                if (!onSlotClick) return
                if ((e.target as HTMLElement).closest('[data-appointment]')) {
                  setHoverSlot(null)
                  return
                }
                setHoverSlot(snapMinute(e))
              }}
              onMouseLeave={() => setHoverSlot(null)}
            >
              {hours.map((hour) => (
                <React.Fragment key={hour}>
                  <div
                    className={classes.hourLine}
                    style={{ top: minutesToPx(hour * 60 - rangeStart) }}
                  />
                  {hour * 60 + 30 < rangeEnd && (
                    <div
                      className={classes.halfHourLine}
                      style={{ top: minutesToPx(hour * 60 + 30 - rangeStart) }}
                    />
                  )}
                </React.Fragment>
              ))}

              {h.open !== h.close && h.open > rangeStart && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: minutesToPx(h.open - rangeStart),
                    background: 'rgba(0,0,0,0.04)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />
              )}
              {h.open !== h.close && h.close < rangeEnd && (
                <div
                  style={{
                    position: 'absolute',
                    top: minutesToPx(h.close - rangeStart),
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.04)',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}
                />
              )}
              {isToday && nowMinute >= rangeStart && nowMinute <= rangeEnd && (
                <div
                  className={classes.nowIndicator}
                  style={{ top: minutesToPx(nowMinute - rangeStart) }}
                />
              )}
              {onSlotClick && hoverSlot !== null && (
                <div
                  style={{
                    position: 'absolute',
                    top: minutesToPx(hoverSlot - rangeStart),
                    height: minutesToPx(60),
                    left: 2,
                    right: 2,
                    borderRadius: 4,
                    background: 'rgba(26, 115, 232, 0.12)',
                    border: '1.5px dashed rgba(26, 115, 232, 0.45)',
                    pointerEvents: 'none',
                    zIndex: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#1a73e8',
                  }}
                >
                  {formatMinuteTime(hoverSlot)} – {formatMinuteTime(hoverSlot + 60)}
                </div>
              )}
              {positioned.map(({ appointment, col, numCols }) => {
                if (!appointment.start || !appointment.end) return null
                const startMin = Math.max(
                  appointment.start.hours() * 60 + appointment.start.minutes(),
                  rangeStart
                )
                const endMin = Math.min(
                  appointment.end.hours() * 60 + appointment.end.minutes(),
                  rangeEnd
                )
                const durationMin = Math.max(endMin - startMin, MIN_SLOT_MINUTES)
                const top = minutesToPx(startMin - rangeStart)
                const height = Math.max(minutesToPx(durationMin), 20)
                const colWidth = 100 / numCols
                const left = `calc(${col * colWidth}% + 2px)`
                const width = `calc(${colWidth}% - 4px)`
                const colors = STATUS_COLORS[appointment.status] ?? DEFAULT_STATUS_COLOR
                const isDragging = dragState?.appointment.guid === appointment.guid
                const direction = getApptDirection(appointment)
                const displayName = getApptDisplayName(appointment)
                return (
                  <Tooltip
                    key={appointment.guid}
                    title={`${
                      direction ? (direction === 'inbound' ? 'IN' : 'OUT') + ' · ' : ''
                    }${displayName} · ${appointment.start.format(
                      'h:mm A'
                    )}–${appointment.end.format('h:mm A')}`}
                    placement="right"
                  >
                    <div
                      data-appointment
                      className={classes.appointmentBlock}
                      style={{
                        top,
                        height,
                        left,
                        width,
                        background: colors.bg,
                        borderLeftColor: colors.border,
                        color: colors.text,
                        opacity: isDragging ? 0.3 : 1,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        touchAction: 'none',
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={handleAppointmentPointerDown(appointment)}
                    >
                      <div className={classes.appointmentName}>{displayName}</div>
                      {height >= 28 && (
                        <div
                          className={classes.appointmentTime}
                          style={{ display: 'flex', alignItems: 'center', gap: 3 }}
                        >
                          <DirectionBadge direction={direction} />
                          {appointment.start.format('h:mm')}–{appointment.end.format('h:mm A')}
                        </div>
                      )}
                      {height >= 44 && appointment.client?.name && (
                        <div className={classes.appointmentTime}>{appointment.client.name}</div>
                      )}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 8,
                          cursor: 'ns-resize',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          touchAction: 'none',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          style={{
                            width: 20,
                            height: 2,
                            borderRadius: 1,
                            background: colors.border,
                            opacity: 0.5,
                          }}
                        />
                      </div>
                    </div>
                  </Tooltip>
                )
              })}
              {dragState?.hasMoved && (
                <div
                  style={{
                    position: 'absolute',
                    top: minutesToPx(dragState.currentStartMinute - rangeStart),
                    height: minutesToPx(dragState.currentEndMinute - dragState.currentStartMinute),
                    left: 2,
                    right: 2,
                    borderRadius: 4,
                    background: 'rgba(26, 115, 232, 0.18)',
                    border: '2px solid rgba(26, 115, 232, 0.6)',
                    boxSizing: 'border-box',
                    pointerEvents: 'none',
                    zIndex: 4,
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 4px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#1a73e8',
                  }}
                >
                  {formatMinuteTime(dragState.currentStartMinute)} –{' '}
                  {formatMinuteTime(dragState.currentEndMinute)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Appointments list */}
      <Divider />
      <div style={{ padding: '12px 16px' }}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
          {sortedAppointments.length === 0
            ? 'No appointments scheduled'
            : `${sortedAppointments.length} Appointment${
                sortedAppointments.length !== 1 ? 's' : ''
              }`}
        </Typography>
        {sortedAppointments.length === 0 ? (
          <Typography variant="body2" style={{ color: '#70757a' }}>
            No appointments scheduled for this day.
          </Typography>
        ) : (
          sortedAppointments.map((appt) => {
            const colors = STATUS_COLORS[appt.status] ?? DEFAULT_STATUS_COLOR
            const link = urlFor(appt)
            const recentlyUpdated = appt.updatedOn && moment().diff(appt.updatedOn, 'seconds') < 8
            const direction = getApptDirection(appt)
            const displayName = getApptDisplayName(appt)
            return (
              <div
                key={appt.guid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  marginBottom: 6,
                  borderRadius: 6,
                  borderLeft: `4px solid ${recentlyUpdated ? '#34A853' : colors.border}`,
                  background: recentlyUpdated ? '#f0faf0' : colors.bg,
                  gap: 12,
                }}
              >
                <div style={{ minWidth: 110, flexShrink: 0 }}>
                  {appt.start && appt.end && (
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, color: recentlyUpdated ? '#2e7d32' : colors.text }}
                    >
                      {appt.start.format('h:mm A')} – {appt.end.format('h:mm A')}
                    </Typography>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <DirectionBadge direction={direction} style={{ fontSize: 10 }} />
                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                      {displayName}
                    </Typography>
                    {recentlyUpdated && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#2e7d32',
                          background: 'rgba(52,168,83,0.15)',
                          borderRadius: 3,
                          padding: '1px 5px',
                        }}
                      >
                        updated
                      </span>
                    )}
                  </div>
                  <div>
                    {appt.client?.name && (
                      <Typography variant="caption" style={{ color: '#70757a' }}>
                        {appt.client.name}
                      </Typography>
                    )}
                    {appt.dock?.name && (
                      <Typography
                        variant="caption"
                        style={{ color: '#70757a', marginLeft: appt.client?.name ? 6 : 0 }}
                      >
                        {appt.client?.name ? '· ' : ''}
                        {appt.dock.name}
                      </Typography>
                    )}
                    {appt.appointmentType && (
                      <Typography variant="caption" style={{ color: '#70757a', marginLeft: 6 }}>
                        · {appt.appointmentType}
                      </Typography>
                    )}
                  </div>
                </div>
                <Chip
                  label={appt.status}
                  size="small"
                  style={{
                    background: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    height: 20,
                    fontSize: 11,
                    flexShrink: 0,
                  }}
                />
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {onEdit && (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => onEdit(appt)}
                    >
                      Edit
                    </Button>
                  )}
                  {link && (
                    <Button size="small" variant="outlined" color="primary" href={link}>
                      View
                    </Button>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      <AppointmentPopover
        appointment={selectedAppointment}
        anchorEl={popoverAnchor}
        onClose={() => {
          setPopoverAnchor(null)
          setSelectedAppointment(null)
        }}
        onEdit={onEdit && selectedAppointment ? () => onEdit(selectedAppointment!) : undefined}
      />
    </div>
  )
}

// ── Stub views ────────────────────────────────────────────────────────────────

const MonthView = () => <div />

// ── Calendar ─────────────────────────────────────────────────────────────────

interface CalendarProps {
  date: moment.Moment
  view?: CalendarView
  defaultHours?: HoursOfOperation
  overrideHours?: ScheduledHoursOfOperation
  appointments: Appointment[]
  maxInbound: number
  maxOutbound: number
  onSlotClick?: (start: moment.Moment) => void
  onEdit?: (appointment: Appointment) => void
  onReschedule?: (appointment: Appointment, newStart: moment.Moment, newEnd: moment.Moment) => void
  onDayClick?: (day: moment.Moment) => void
  loading?: boolean
}

export const Calendar = (props: CalendarProps) => {
  const {
    date,
    view = 'week',
    defaultHours = defaultHoursOfOperation,
    overrideHours,
    appointments,
    maxInbound,
    maxOutbound,
    onSlotClick,
    onEdit,
    onReschedule,
    onDayClick,
    loading,
  } = props

  const viewProps: ViewProps = {
    date,
    defaultHours,
    overrideHours,
    appointments,
    maxInbound,
    maxOutbound,
    onSlotClick,
    onEdit,
    onReschedule,
    onDayClick,
    loading,
  }

  if (view === 'week') return <WeekView {...viewProps} />
  if (view === 'day') return <DayView {...viewProps} />
  return <MonthView />
}
